import { useState } from 'react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Check, X } from 'lucide-react'
import toast from 'react-hot-toast'
import authService from '../../services/authService'
import React from 'react'

interface ChangePasswordModalProps {
  isOpen: boolean
  onClose: () => void
}

interface PasswordValidation {
  key: string
  test: boolean
  message: string
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [passwordMismatch, setPasswordMismatch] = useState(false)

  const passwordValidations: PasswordValidation[] = [
    {
      key: 'length',
      test: newPassword.length >= 8 && newPassword.length <= 20,
      message: 'Debe tener entre 8 y 20 caracteres',
    },
    {
      key: 'special',
      test: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
      message: 'Debe contener al menos un carácter especial',
    },
    {
      key: 'number',
      test: /[0-9]/.test(newPassword),
      message: 'Debe contener al menos un número (0-9)',
    },
    {
      key: 'uppercase',
      test: /[A-Z]/.test(newPassword),
      message: 'Debe tener al menos una mayúscula (A-Z)',
    },
  ]

  const allPasswordValidationsPassed = passwordValidations.every(v => v.test)
  const passwordsMatch =
    newPassword === confirmPassword && confirmPassword !== ''
  const isFormValid =
    allPasswordValidationsPassed && passwordsMatch && newPassword !== ''

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordMismatch(false)

    if (!isFormValid) return

    if (newPassword !== confirmPassword) {
      setPasswordMismatch(true)
      return
    }

    setIsSubmitting(true)

    try {
      await authService.updatePassword(newPassword, confirmPassword)
      toast.success('Contraseña actualizada correctamente')
      handleClose()
    } catch (error: any) {
      console.error('Error al cambiar contraseña:', error)
      toast.error(
        error?.response?.data?.message || 'Error al cambiar la contraseña'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setNewPassword('')
    setConfirmPassword('')
    setShowNewPassword(false)
    setShowConfirmPassword(false)
    setPasswordMismatch(false)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      closeOnOverlay={false}
    >
      <div className="p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Cambiar contraseña
        </h2>
        <p className="text-gray-600 mb-6">
          Crea una contraseña nueva, segura y personalizada cumpliendo las
          características solicitadas.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nueva contraseña
            </label>
            <Input
              type="password"
              placeholder="Escribe aquí"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              showPassword={showNewPassword}
              onTogglePassword={() => setShowNewPassword(!showNewPassword)}
            />

            {/* Password validation indicators */}
            {newPassword && (
              <div className="mt-3 space-y-1">
                {passwordValidations.map(validation => (
                  <div
                    key={validation.key}
                    className="flex items-center text-sm"
                  >
                    {validation.test ? (
                      <Check className="text-green-500 mr-2" size={16} />
                    ) : (
                      <X className="text-red-500 mr-2" size={16} />
                    )}
                    <span
                      className={
                        validation.test ? 'text-green-700' : 'text-gray-600'
                      }
                    >
                      {validation.message}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirma tu nueva contraseña
            </label>
            <Input
              type="password"
              placeholder="Escribe aquí"
              value={confirmPassword}
              onChange={e => {
                setConfirmPassword(e.target.value)
                setPasswordMismatch(false)
              }}
              showPassword={showConfirmPassword}
              onTogglePassword={() =>
                setShowConfirmPassword(!showConfirmPassword)
              }
              error={
                passwordMismatch ? 'Las contraseñas no coinciden' : undefined
              }
            />
          </div>

          <div className="flex gap-3 pt-2 max-h-12">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-sm"
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              className="flex-1 px-4 py-2 text-sm"
              disabled={!isFormValid || isSubmitting}
              isLoading={isSubmitting}
            >
              Guardar contraseña
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}
